import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, Polyline, Marker, OverlayView } from "@react-google-maps/api";
import { useGlidingPosition } from "../hooks/useGlidingPosition";
import { simplifyForDisplay, splitRouteSegments } from "../utils/routeSegments";
import { haversineMeters } from "../utils/fleetMapPath";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_VERSION } from "../utils/googleMapsLoader";
import { useAuth } from "../contexts/AuthContext";
import {
  announceToBusParents,
  callBusDriver,
  CallDriverPerson,
} from "../services/fleetContactApi";
import { busPhrase } from "../fleet/busPhrase";
import "./BusLiveMapPanel.css";

/* A run is morning, evening — or a REFUEL trip. Every label here used to be
   `morning ? "Morning" : "Evening"`, so a driver's refuel trip read "Evening
   route · LEAF" on the Live map (Gabriel, 2026-08-22, refuel drive).
   (Below ALL imports — CRA's eslint `import/first` fails the build otherwise.) */
function runLabel(type: string | null | undefined): string {
  const t = String(type ?? "").toLowerCase();
  if (t === "morning") return "Morning";
  if (t === "refuel") return "Refuel";
  if (t === "evening") return "Evening";
  return t ? t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, " ") : "Trip";
}
function runChipClass(type: string | null | undefined): string {
  const t = String(type ?? "").toLowerCase();
  if (t === "morning") return "chip-morning";
  if (t === "refuel") return "chip-refuel";
  return "chip-evening";
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BusChild {
  id: string;
  name: string;
  status: string;
  parentPhone?: string;
  parentId?: string;
  classLabel?: string;
}

interface BusRoute {
  type: string | null;
  status: string | null;
  started: Date | null;
}

interface BusEntry {
  id: string;
  plate: string;
  name: string;
  school?: string;
  liveLocation?: { lat: number; lng: number } | null;
  liveLocationUpdatedAt?: number | null;
  locationError?: string | null;
  gpsRoutePoints?: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[];
  route: BusRoute;
  children: BusChild[];
  trafficDetected?: boolean;
  status: string;
}

export interface BusLiveMapPanelProps {
  buses: BusEntry[];
  fleetOrgCode: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NAIROBI_CENTER = { lat: -1.286, lng: 36.817 };
const ROUTE_BLUE = "#2563eb";
const AV_COLORS = ["#2563eb", "#0d9488", "#7c3aed", "#d97706", "#db2777", "#16a34a", "#4f46e5", "#dc2626"];

type StatusKey = "live" | "stale" | "lost" | "idle";

const SUBTLE_MAP_STYLES: google.maps.MapTypeStyle[] = [
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#e0e0e0" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#bcdcec" }] },
  { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f3f4f7" }] },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function secondsSince(epochMs: number | null | undefined): number | null {
  if (!epochMs) return null;
  return Math.max(0, (Date.now() - epochMs) / 1000);
}

/** Compact age for the rail chip: "12s" / "4m" / "1h". */
function shortAge(sec: number | null): string | null {
  if (sec === null) return null;
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  return `${Math.round(sec / 3600)}h`;
}

/** Long age for the status pill: "updated 12s ago". */
function friendlyAge(sec: number): string {
  if (sec < 60) return `${Math.round(sec)}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  return `${Math.round(sec / 3600)}h ago`;
}

/** Map a bus to one of the four live-status buckets used by the rail + marker. */
function statusKeyFor(bus: BusEntry): StatusKey {
  // "refuel" is the status the app stamps for a refuel trip — a bus on one is
  // on the road, not idle (Gabriel, 2026-08-23: the Live map said "Idle", kept
  // it out of "On route", and did not focus it).
  const routeActive = bus.route.status === "active" || bus.route.status === "collecting" || bus.route.status === "refuel";
  if (!routeActive) return "idle";
  if (bus.locationError) return "lost";
  const sec = secondsSince(bus.liveLocationUpdatedAt);
  if (sec === null) return "idle";
  // 120 -> 60s (Gabriel, 2026-07-28 drive test): a killed app kept reading
  // "Live" for over two minutes. Writes land every ~4.5s, so 60s is a dozen
  // missed writes -- comfortably past noise, and a pin that claims to be live
  // while the driver's app is dead is the one thing this rail must never do.
  // The parent app's two map screens use the same 60s; keep all three in step.
  if (sec > 300) return "lost";
  if (sec > 60) return "stale";
  return "live";
}
const STATUS_LABEL: Record<StatusKey, string> = { live: "Live", stale: "Stale", lost: "Signal lost", idle: "Idle" };

function isMorning(type: string | null): boolean {
  return (type ?? "").toLowerCase() === "morning";
}

function pickedCount(children: BusChild[]): number {
  return children.filter((c) => c.status === "picked_up").length;
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";
}

function fmtTime(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function elapsedMin(started: Date | null): number | null {
  if (!started) return null;
  return Math.max(0, Math.round((Date.now() - started.getTime()) / 60000));
}

function childPill(status: string): { cls: string; label: string } {
  switch (status) {
    case "picked_up": return { cls: "pill-picked", label: "Picked up" };
    case "dropped_off": return { cls: "pill-dropped", label: "Dropped off" };
    case "absent": return { cls: "pill-absent", label: "Absent" };
    default: return { cls: "pill-pending", label: "Pending" };
  }
}

function childSub(c: BusChild): string {
  if (c.classLabel) return c.classLabel;
  switch (c.status) {
    case "picked_up": return "On board";
    case "dropped_off": return "Dropped off";
    case "absent": return "Absent today";
    default: return "Awaiting pickup";
  }
}

/* How far this bus has driven so far, live, in km.
 *
 * Gabriel asked on 2026-08-09, looking at this screen mid-drive: "is there a
 * place it should be showing the distance?" — there wasn't one. The console
 * could tell him how many children were aboard and how long the trip had run,
 * but not how far it had gone, which is the figure fuel and pay are argued
 * from.
 *
 * Measured off the same GPS breadcrumbs the blue trail is drawn from, so the
 * number and the line can never disagree, and split at app-kill resume markers
 * so a closed-app gap is not billed as distance travelled. It is a live
 * estimate: the finished trip's figure is the road-snapped one the backend
 * computes afterwards, which is why this is labelled "so far".
 */
function travelledKm(
  points: { lat: number; lng: number; gapBefore?: boolean }[] | null | undefined
): number | null {
  if (!points || points.length < 2) return null;
  let m = 0;
  for (let i = 1; i < points.length; i++) {
    // A `gapBefore` point is the first fix after the app came back. The straight
    // line to it crosses however far the bus went unobserved — never counted.
    if (points[i].gapBefore) continue;
    m += haversineMeters(points[i - 1], points[i]);
  }
  return m > 0 ? m / 1000 : null;
}

const BUS_ICON_PATH =
  "M4 16V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a1 1 0 0 1-1 1h-1a2 2 0 1 1-4 0H8a2 2 0 1 1-4 0H3a1 1 0 0 1-1-1Zm2-9v4h12V7Z";

// ---------------------------------------------------------------------------
// Center map (rendered only when the Maps API is loaded)
// ---------------------------------------------------------------------------

function MapCanvas({ bus, statusKey }: { bus: BusEntry; statusKey: StatusKey }) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [hybrid, setHybrid] = useState(false);

  const routePoints = bus.gpsRoutePoints ?? [];
  // Split at app-kill resume markers so the trail never draws a straight line
  // across the closed-app gap.
  // Draw-time smoothing only — the stored trail keeps every point ("our live
  // still looks bad", Gabriel 2026-08-24; snapping can only happen at trip end).
  const routeSegments = useMemo(
    () => splitRouteSegments(routePoints).map((s) => simplifyForDisplay(s)),
    [routePoints],
  );
  const liveLoc = bus.liveLocation ?? null;
  const rawMarkerPos = liveLoc ?? (routePoints.length ? routePoints[routePoints.length - 1] : null);
  // Drawn position only — the trail, the distance and every figure still use
  // the real fixes. See useGlidingPosition.
  const markerPos = useGlidingPosition(rawMarkerPos) ?? rawMarkerPos;

  const center = useMemo(() => {
    if (liveLoc) return liveLoc;
    if (routePoints.length) return routePoints[Math.floor(routePoints.length / 2)];
    return NAIROBI_CENTER;
  }, [liveLoc, routePoints]);

  /* WHERE THE MAP OPENS — not where it is dragged back to.
     `center` recomputes on every live fix, and passing it to <GoogleMap> as a
     prop makes it CONTROLLED: the library re-applies it on each change and
     yanks the viewport back, about every five seconds. That happens
     declaratively, above the follow logic, so the FOLLOW_RESUME_MS guard below
     — which is correct, and deliberately skips panTo while a human is looking
     around — could never take effect. The fight Gabriel described on 08-02 was
     still there, just moved (review, 09-06).
     Frozen at first render so the map opens in the right place; every later
     move goes through the guarded panTo, and Recenter is always one tap away. */
  const initialCenterRef = useRef(center);

  /* Following, and how to get out of it.
   *
   * This panel used to panTo on EVERY live fix with no guard at all, and fixes
   * land about every five seconds. So looking at where a trip started was a
   * fight: pan away, get yanked back, pan away again. Gabriel, 2026-08-02:
   * "it refreshes too quickly to recenter back to the bus, it should be like
   * 10 sec if it is left untouched, then it should recenter."
   *
   * `BusMiniMap` already had a drag guard — it just stops following until you
   * press re-centre. This is that idea with his timer, and the same rule is
   * mirrored back onto the mini map so the two behave alike. */
  const FOLLOW_RESUME_MS = 10_000;
  const lastInteractionRef = useRef(0);
  const resumeTimerRef = useRef<number | null>(null);
  const markerPosRef = useRef(markerPos);
  markerPosRef.current = markerPos;

  const noteInteraction = useCallback(() => {
    lastInteractionRef.current = Date.now();
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    // Re-centre on the timer as well as on the next fix: a parked bus stops
    // producing fixes, and "wait ten seconds" should still bring it back.
    resumeTimerRef.current = window.setTimeout(() => {
      const map = mapRef.current;
      const p = markerPosRef.current;
      if (map && p) map.panTo(p);
    }, FOLLOW_RESUME_MS);
  }, []);

  useEffect(() => () => {
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
  }, []);

  // Follow the live location as it moves — unless a human is looking around.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !liveLoc) return;
    if (Date.now() - lastInteractionRef.current < FOLLOW_RESUME_MS) return;
    map.panTo(liveLoc);
    if ((map.getZoom() ?? 0) < 14) map.setZoom(15);
  }, [liveLoc?.lat, liveLoc?.lng]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    /* `dragstart`, not `center_changed`: the generic event fires on our own
       panTo, so the map would follow exactly once and then look broken. Same
       trap documented in BusMiniMap. */
    map.addListener("dragstart", noteInteraction);
    const pts = [...(bus.gpsRoutePoints ?? []), ...(bus.liveLocation ? [bus.liveLocation] : [])];
    if (pts.length >= 2) {
      const bounds = new google.maps.LatLngBounds();
      pts.forEach((p) => bounds.extend(p));
      /* A trip that has just started has two points twelve metres apart, and
         fitBounds on that zooms to street-number level — "sometimes it starts
         zoomed in, like, maximum" (Gabriel, 2026-08-22). Measure the span:
         under ~400 m there is nothing to fit, so centre at a street zoom; past
         that fit, but never tighter than a road-level 17. */
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const midLat = ((ne.lat() + sw.lat()) / 2) * (Math.PI / 180);
      const spanM = Math.hypot(
        (ne.lat() - sw.lat()) * 111_320,
        (ne.lng() - sw.lng()) * 111_320 * Math.cos(midLat),
      );
      if (spanM < 400) {
        map.setCenter(bounds.getCenter());
        map.setZoom(15);
      } else {
        map.fitBounds(bounds, 64);
        google.maps.event.addListenerOnce(map, "idle", () => {
          if ((map.getZoom() ?? 0) > 17) map.setZoom(17);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recenter = () => {
    const map = mapRef.current;
    // An explicit re-centre means "resume following now", so drop the pause.
    lastInteractionRef.current = 0;
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    if (map && markerPos) { map.panTo(markerPos); map.setZoom(15); }
  };
  const zoom = (delta: number) => {
    const map = mapRef.current;
    // Zooming out to see more road is looking around too.
    noteInteraction();
    if (map) map.setZoom((map.getZoom() ?? 14) + delta);
  };

  const markerColorClass = statusKey === "live" || statusKey === "idle" ? "" : statusKey;

  return (
    <>
      <GoogleMap
        mapContainerClassName="map-canvas"
        center={initialCenterRef.current}
        zoom={14}
        onLoad={onLoad}
        options={{
          styles: hybrid ? undefined : SUBTLE_MAP_STYLES,
          mapTypeId: hybrid ? "hybrid" : "roadmap",
          disableDefaultUI: true,
          clickableIcons: false,
          gestureHandling: "greedy",
        }}
      >
        {/* Where the trip STARTED — the line's other end has the live bus. */}
        {routeSegments[0] && routeSegments[0].length >= 2 && (
          <Marker
            position={{ lat: routeSegments[0][0].lat, lng: routeSegments[0][0].lng }}
            zIndex={2}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 6.5,
              fillColor: "#ffffff",
              fillOpacity: 1,
              strokeColor: ROUTE_BLUE,
              strokeWeight: 3.5,
            }}
          />
        )}
        {/* travelled route — soft glow + solid blue (the live bus's trail), one
            pair per gap-free segment so an app-kill resume never draws a straight
            line across the closed-app gap. Inferred gap-fill segments (estimated)
            render as a single dotted line instead of the glow+solid pair. */}
        {routeSegments.flatMap((seg, i) =>
          seg.length < 2
            ? []
            : seg[0]?.estimated
            ? [
                <Polyline
                  key={`est-${i}`}
                  path={seg}
                  options={{
                    strokeColor: ROUTE_BLUE,
                    strokeOpacity: 0,
                    geodesic: true,
                    zIndex: 2,
                    icons: [
                      {
                        icon: {
                          path: google.maps.SymbolPath.CIRCLE,
                          fillColor: ROUTE_BLUE,
                          fillOpacity: 1,
                          strokeColor: ROUTE_BLUE,
                          strokeOpacity: 1,
                          scale: 2.6,
                        },
                        offset: "0",
                        repeat: "14px",
                      },
                    ],
                  }}
                />,
              ]
            : [
                <Polyline key={`glow-${i}`} path={seg} options={{ strokeColor: ROUTE_BLUE, strokeWeight: 12, strokeOpacity: 0.16, geodesic: true, zIndex: 1 }} />,
                <Polyline key={`solid-${i}`} path={seg} options={{ strokeColor: ROUTE_BLUE, strokeWeight: 5, strokeOpacity: 1, geodesic: true, zIndex: 2 }} />,
              ]
        )}

        {/* start dot */}
        {routePoints.length >= 2 && (
          <Marker
            position={routePoints[0]}
            icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 6, fillColor: "#16a34a", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 }}
          />
        )}

        {/* animated bus marker */}
        {markerPos && (
          <OverlayView position={markerPos} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET} getPixelPositionOffset={() => ({ x: 0, y: 0 })}>
            <div className={`pear-livemap-marker ${markerColorClass}`}>
              <div className="ring" />
              <div className="disc"><div className="core"><svg viewBox="0 0 24 24"><path d={BUS_ICON_PATH} /></svg></div></div>
            </div>
          </OverlayView>
        )}
      </GoogleMap>

      {/* Legend for the dotted trail — shown ONLY when this trip actually has an
          inferred segment. Without it a dotted line is unexplained: an admin
          reads a gap in the trail as a tracking failure, when it means the app
          was closed and this stretch is Directions' best guess, not GPS. */}
      {routeSegments.some((seg) => seg.length >= 2 && seg[0]?.estimated) && (
        <div
          style={{
            position: "absolute", left: 16, bottom: 16, zIndex: 5,
            display: "flex", alignItems: "center", gap: 8,
            background: "rgba(255,255,255,0.94)", borderRadius: 8,
            padding: "7px 11px", fontSize: 12, color: "#1c1730",
            boxShadow: "0 1px 6px rgba(0,0,0,0.16)", maxWidth: 260,
          }}
        >
          <svg width="26" height="8" viewBox="0 0 26 8" style={{ flexShrink: 0 }}>
            <circle cx="3" cy="4" r="2.4" fill={ROUTE_BLUE} />
            <circle cx="13" cy="4" r="2.4" fill={ROUTE_BLUE} />
            <circle cx="23" cy="4" r="2.4" fill={ROUTE_BLUE} />
          </svg>
          <span><b>Dotted</b> = estimated. The app was closed here, so this stretch is the likely road route, not recorded GPS.</span>
        </div>
      )}

      {/* floating map tools (right) */}
      <div className="map-tools" style={{ position: "absolute", top: 16, right: 16, zIndex: 5 }}>
        <div className="tool-group glass">
          <button title="Recenter" onClick={recenter}><svg viewBox="0 0 24 24"><path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0-6 2 3h-1v2h-2V5H10l2-3Zm0 20-2-3h1v-2h2v2h1l-2 3ZM2 12l3-2v1h2v2H5v1l-3-2Zm20 0-3 2v-1h-2v-2h2v-1l3 2Z" /></svg></button>
          <button title="Satellite / map" onClick={() => setHybrid((v) => !v)}><svg viewBox="0 0 24 24"><path d="m12 3 9 5-9 5-9-5 9-5Zm0 7.2L5.5 6.6 3 8l9 5 9-5-2.5-1.4L12 10.2ZM3 13l9 5 9-5 2 1.1-11 6.1L1 14.1 3 13Z" /></svg></button>
        </div>
        <div className="tool-group glass">
          <button title="Zoom in" onClick={() => zoom(1)}><svg viewBox="0 0 24 24"><path d="M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6V5Z" /></svg></button>
          <button title="Zoom out" onClick={() => zoom(-1)}><svg viewBox="0 0 24 24"><path d="M5 11h14v2H5z" /></svg></button>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function BusLiveMapPanel({ buses, fleetOrgCode }: BusLiveMapPanelProps): JSX.Element {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
  const { currentUser } = useAuth();
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });

  const [query, setQuery] = useState("");
  const [seg, setSeg] = useState<"all" | "onroute" | "idle">("all");
  const [selectedId, setSelectedId] = useState<string>("");

  const [showAnnounce, setShowAnnounce] = useState(false);
  const [announceMsg, setAnnounceMsg] = useState("");
  const [announceSending, setAnnounceSending] = useState(false);
  const [announceResult, setAnnounceResult] = useState<string | null>(null);

  const [showCall, setShowCall] = useState(false);
  const [callLoading, setCallLoading] = useState(false);
  const [callError, setCallError] = useState<string | null>(null);
  const [callNote, setCallNote] = useState<string | null>(null);
  const [callDrivers, setCallDrivers] = useState<CallDriverPerson[]>([]);

  // Re-tick every 15s so age labels ("12s ago") and status buckets stay fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);

  const onRouteCount = useMemo(() => buses.filter((b) => statusKeyFor(b) !== "idle").length, [buses]);
  const idleCount = buses.length - onRouteCount;

  // Sort: active routes first, then by name.
  const sorted = useMemo(
    () =>
      [...buses].sort((a, b) => {
        const rank = (x: BusEntry) => (statusKeyFor(x) === "idle" ? 1 : 0);
        return rank(a) - rank(b) || a.name.localeCompare(b.name);
      }),
    [buses]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter((b) => {
      const k = statusKeyFor(b);
      if (seg === "onroute" && k === "idle") return false;
      if (seg === "idle" && k !== "idle") return false;
      if (q && !(`${b.name} ${b.plate}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [sorted, query, seg]);

  // Keep a valid selection (prefer first on-route bus).
  useEffect(() => {
    if (selectedId && buses.some((b) => b.id === selectedId)) return;
    setSelectedId(sorted[0]?.id ?? "");
  }, [buses, sorted, selectedId]);

  const bus = useMemo(() => buses.find((b) => b.id === selectedId) ?? sorted[0] ?? null, [buses, sorted, selectedId]);
  const busStatus = bus ? statusKeyFor(bus) : "idle";
  const ageSec = bus ? secondsSince(bus.liveLocationUpdatedAt) : null;

  const total = bus?.children.length ?? 0;
  const picked = bus ? pickedCount(bus.children) : 0;
  const pendingCount = total - picked;
  const boardPct = total > 0 ? Math.round((picked / total) * 100) : 0;
  const morning = isMorning(bus?.route.type ?? null);
  const kmSoFar = travelledKm(bus?.gpsRoutePoints);

  const openAnnounce = useCallback(() => {
    if (!bus) return;
    setAnnounceMsg("");
    setAnnounceResult(null);
    setShowAnnounce(true);
  }, [bus]);

  const sendAnnounce = useCallback(async () => {
    if (!bus || !fleetOrgCode || !announceMsg.trim() || announceSending) return;
    setAnnounceSending(true);
    setAnnounceResult(null);
    try {
      const res = await announceToBusParents(fleetOrgCode, bus.id, announceMsg.trim());
      if (!res.ok) {
        setAnnounceResult(res.message || "Failed to send");
        return;
      }
      setAnnounceResult(`Sent to ${res.sent ?? 0} of ${res.total ?? 0} parents`);
      if ((res.sent ?? 0) > 0) setAnnounceMsg("");
    } finally {
      setAnnounceSending(false);
    }
  }, [bus, fleetOrgCode, announceMsg, announceSending]);

  const openCallDriver = useCallback(async () => {
    if (!bus || !fleetOrgCode || callLoading) return;
    setShowCall(true);
    setCallLoading(true);
    setCallError(null);
    setCallNote(null);
    setCallDrivers([]);
    try {
      const res = await callBusDriver(
        fleetOrgCode,
        bus.id,
        currentUser?.displayName || currentUser?.email || undefined,
      );
      if (!res.ok) {
        setCallError(res.message || "Could not reach the driver.");
        setCallDrivers(res.drivers || []);
        return;
      }
      setCallDrivers(res.drivers || []);
      setCallNote(res.message || null);
    } finally {
      setCallLoading(false);
    }
  }, [bus, fleetOrgCode, callLoading, currentUser?.displayName, currentUser?.email]);

  return (
    <div className="pear-livemap">
      {/* ───────────── Left rail ───────────── */}
      <aside className="fleet-rail">
        <div className="rail-head">
          <div className="rail-title-row">
            <div className="rail-title">Live Fleet</div>
            <div className="rail-count">{onRouteCount} on route</div>
          </div>
          <div className="rail-search">
            <svg viewBox="0 0 24 24"><path d="M10 4a6 6 0 1 0 3.5 10.9l4.3 4.3 1.4-1.4-4.3-4.3A6 6 0 0 0 10 4Zm0 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z" /></svg>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a bus" />
          </div>
          <div className="seg">
            <button className={seg === "all" ? "active" : ""} onClick={() => setSeg("all")}>All · {buses.length}</button>
            <button className={seg === "onroute" ? "active" : ""} onClick={() => setSeg("onroute")}>On route · {onRouteCount}</button>
            <button className={seg === "idle" ? "active" : ""} onClick={() => setSeg("idle")}>Idle · {idleCount}</button>
          </div>
        </div>
        <div className="rail-list">
          {filtered.length === 0 ? (
            <div className="rail-empty">No buses match.</div>
          ) : (
            filtered.map((b) => {
              const k = statusKeyFor(b);
              const sec = secondsSince(b.liveLocationUpdatedAt);
              const age = shortAge(sec);
              const tot = b.children.length;
              const pk = pickedCount(b.children);
              const pct = tot > 0 ? Math.round((pk / tot) * 100) : 0;
              const clr = AV_COLORS[buses.indexOf(b) % AV_COLORS.length];
              const m = isMorning(b.route.type);
              return (
                <div
                  key={b.id}
                  className={`bus-card${b.id === selectedId ? " selected" : ""}`}
                  style={{ ["--clr" as string]: k === "idle" ? "#99a1ae" : clr }}
                  onClick={() => setSelectedId(b.id)}
                >
                  <div className="bc-top">
                    <div>
                      <div className="bc-name">{b.name}</div>
                      <div className="bc-plate mono">{b.plate}</div>
                    </div>
                    <span className={`bc-status st-${k}`}><span className="dot" />{STATUS_LABEL[k]}{age ? ` · ${age}` : ""}</span>
                  </div>
                  <div className="bc-meta">
                    {b.route.type && <span className={`chip ${runChipClass(b.route.type)}`}>{runLabel(b.route.type)}</span>}
                    <span className="bc-board">{pk}/{tot} on board</span>
                  </div>
                  <div className="bc-prog"><div style={{ width: `${k === "idle" ? 0 : pct}%` }} /></div>
                  <div className="bc-progmeta">
                    <span>{k === "idle" ? "Not started" : `${pct}% picked up`}</span>
                    <span>{b.route.started ? `Started ${fmtTime(b.route.started)}` : "—"}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* ───────────── Center map ───────────── */}
      <div className="map-wrap">
        {!apiKey.trim() ? (
          <div className="map-state">
            <div className="ms-title">Google Maps API key not configured</div>
            <div className="ms-sub">Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in <code>.env</code> to show live tracking.</div>
          </div>
        ) : loadError ? (
          <div className="map-state"><div className="ms-title">Failed to load Google Maps</div><div className="ms-sub">{loadError.message}</div></div>
        ) : !isLoaded ? (
          <div className="map-state"><div className="ms-sub">Loading map…</div></div>
        ) : bus ? (
          <MapCanvas bus={bus} statusKey={busStatus} />
        ) : (
          <div className="map-state"><div className="ms-title">No buses yet</div><div className="ms-sub">Buses appear here once added to your fleet.</div></div>
        )}

        {/* top crumb + status pill (overlay) */}
        {bus && (
          <div className="map-topbar">
            <div className="map-crumb glass">
              <div className="crumb-icon"><svg viewBox="0 0 24 24"><path d={BUS_ICON_PATH} /></svg></div>
              <div>
                <div className="crumb-name">Bus <span className="mono">{bus.plate}</span></div>
                <div className="crumb-sub">
                  {bus.route.type ? (bus.route.type === "refuel" ? "Refuel trip" : `${runLabel(bus.route.type)} route`) : "No active route"}
                  {bus.school ? ` · ${bus.school}` : ""}
                </div>
              </div>
              <div className="crumb-div" />
              <div className="crumb-stat"><b>{picked}<small> / {total}</small></b><span>On board</span></div>
              <div className="crumb-div" />
              <div className="crumb-stat"><b>{fmtTime(bus.route.started)}</b><span>Started</span></div>
              {kmSoFar !== null && (
                <>
                  <div className="crumb-div" />
                  <div className="crumb-stat"><b>{kmSoFar.toFixed(1)}<small> km</small></b><span>Distance</span></div>
                </>
              )}
              <div className="crumb-div" />
              <div className={`status-pill ${busStatus}`}>
                <span className="dot" />{STATUS_LABEL[busStatus]}
                {ageSec !== null && <span className="age">· updated {friendlyAge(ageSec)}</span>}
              </div>
            </div>
          </div>
        )}

        {/* banners -- both sit in the same slot, so at most one renders.
            2026-09-08: the signal banner used to yield to the traffic one. Nothing
            clears trafficDetected when the driver's app is killed (only route
            start/finish do), so a stale `true` hid a genuine "Signal lost"
            indefinitely. Signal always wins, and the traffic notice only shows
            while the feed is Live -- a bus we cannot hear from is not "in traffic",
            it is silent. */}
        {bus && busStatus === "lost" && (
          <div className="map-banner lost">
            <svg viewBox="0 0 24 24"><path d="M2 4 22 24M12 18a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z" /></svg>
            {bus.locationError ? `GPS error: ${bus.locationError}` : "GPS signal lost — location may be stale"}
          </div>
        )}
        {bus?.trafficDetected && busStatus === "live" && (
          <div className="map-banner traffic">
            <svg viewBox="0 0 24 24"><path d="M12 2 1 21h22L12 2Zm0 4 7.5 13H4.5L12 6Zm-1 5v4h2v-4h-2Zm0 5v2h2v-2h-2Z" /></svg>
            Traffic delay detected on this route
          </div>
        )}

        {/* bottom active-trip card */}
        {bus && (
          <div className="trip-card glass">
            <div className="tc-head">
              <h4>Active trip</h4>
              {bus.route.type && <span className={`chip ${runChipClass(bus.route.type)}`}>{runLabel(bus.route.type)}</span>}
            </div>
            <div className="tc-route">
              <div className="tc-stop"><span className="pin start" /><span className="lbl">{bus.route.type === "refuel" ? (bus.school || "Depot") : morning ? "Pickup" : bus.school || "School"}</span></div>
              <div className="tc-line" />
              <div className="tc-stop"><span className="lbl">{bus.route.type === "refuel" ? "Station & back" : morning ? (bus.school || "School") : "Drop-off"}</span><span className="pin end" /></div>
            </div>
            <div className="tc-grid">
              <div className="tc-cell"><div className="k">Started</div><div className="v">{fmtTime(bus.route.started)}</div></div>
              <div className="tc-cell"><div className="k">Elapsed</div><div className="v">{elapsedMin(bus.route.started) ?? "—"}<small> min</small></div></div>
              <div className="tc-cell"><div className="k">On board</div><div className="v">{bus.route.type === "refuel" ? "—" : <>{picked}<small> / {total}</small></>}</div></div>
              <div className="tc-cell">
                <div className="k">Distance so far</div>
                <div className="v">{kmSoFar !== null ? kmSoFar.toFixed(1) : "—"}<small> km</small></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ───────────── Right detail panel ───────────── */}
      <aside className="detail-panel">
        {bus?.route.type === "refuel" ? (
          /* A refuel trip carries no children — "0 / 5 expected · 5 pending"
             read as five children waiting somewhere (Gabriel, 2026-08-23). */
          <div className="dp-head">
            <div className="dp-eyebrow">Refuel trip</div>
            <div className="dp-board"><b>—</b><span className="frac">no children on this run</span></div>
          </div>
        ) : (
          <div className="dp-head">
            <div className="dp-eyebrow">Children on board</div>
            <div className="dp-board"><b>{picked}</b><span className="frac">/ {total} expected</span></div>
            <div className="dp-bar"><div className="got" style={{ width: `${boardPct}%` }} /></div>
            <div className="dp-legend">
              <span><i style={{ background: "var(--green)" }} />{picked} picked up</span>
              <span><i style={{ background: "var(--ink-3)" }} />{pendingCount} pending</span>
            </div>
          </div>
        )}
        <div className="kids">
          {!bus || total === 0 || bus.route.type === "refuel" ? (
            <div className="kids-empty">{bus && bus.route.type === "refuel" ? "No children ride on a refuel trip." : "No children assigned to this bus."}</div>
          ) : (
            bus.children.map((c, i) => {
              const pill = childPill(c.status);
              return (
                <div className="kid" key={c.id || `${c.name}-${i}`}>
                  <div className="kid-av" style={{ background: AV_COLORS[i % AV_COLORS.length] }}>{initials(c.name)}</div>
                  <div className="kid-info">
                    <div className="kid-name">{c.name || "Child"}</div>
                    <div className="kid-sub">{childSub(c)}</div>
                  </div>
                  <span className={`kid-pill ${pill.cls}`}>{pill.label}</span>
                </div>
              );
            })
          )}
        </div>
        <div className="dp-foot">
          <button
            type="button"
            className="btn btn-ghost"
            disabled={!bus || !fleetOrgCode || callLoading}
            onClick={() => { void openCallDriver(); }}
          >
            <svg viewBox="0 0 24 24"><path d="M6.6 10.8a15 15 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11 11 0 0 0 3.5.56 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11 11 0 0 0 .56 3.5 1 1 0 0 1-.25 1l-2.2 2.3Z" /></svg>
            Call driver
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!bus || !fleetOrgCode}
            onClick={openAnnounce}
          >
            <svg viewBox="0 0 24 24"><path d="M3 11l18-8-8 18-2.5-7.5L3 11Z" /></svg>
            Announce
          </button>
        </div>
      </aside>

      {showAnnounce && bus && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 6200 }}
          onClick={() => !announceSending && setShowAnnounce(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 440, width: "90%", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontFamily: "Instrument Serif, serif", fontSize: 22, marginBottom: 8 }}>Send Announcement</h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              All parents on {busPhrase(bus.name)} will receive this as a push notification and in their in-app inbox.
            </p>
            <textarea
              value={announceMsg}
              onChange={(e) => setAnnounceMsg(e.target.value)}
              placeholder="e.g. Bus will be 15 minutes late today."
              rows={4}
              maxLength={500}
              style={{ width: "100%", padding: 12, border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, fontFamily: "Geist, sans-serif", resize: "vertical", marginBottom: 8 }}
            />
            <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 16 }}>{announceMsg.length}/500 characters</div>
            {announceResult && (
              <div style={{ marginBottom: 16, fontSize: 14, color: announceResult.startsWith("Sent") ? "#16a34a" : "#dc2626" }}>
                {announceResult}
              </div>
            )}
            <div style={{ display: "flex", gap: 12 }}>
              <button
                type="button"
                onClick={() => { void sendAnnounce(); }}
                disabled={!announceMsg.trim() || announceSending}
                style={{
                  padding: "10px 20px",
                  background: announceMsg.trim() && !announceSending ? "#2563eb" : "#cbd5e1",
                  color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
                  cursor: announceMsg.trim() && !announceSending ? "pointer" : "not-allowed",
                  fontFamily: "Geist, sans-serif",
                }}
              >
                {announceSending ? "Sending…" : "Send"}
              </button>
              <button
                type="button"
                onClick={() => { setShowAnnounce(false); setAnnounceResult(null); }}
                disabled={announceSending}
                style={{
                  padding: "10px 20px", background: "#f1f5f9", color: "#334155", border: "none",
                  borderRadius: 8, fontSize: 14, fontWeight: 600,
                  cursor: announceSending ? "not-allowed" : "pointer", fontFamily: "Geist, sans-serif",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showCall && bus && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 6200 }}
          onClick={() => !callLoading && setShowCall(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 440, width: "90%", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontFamily: "Instrument Serif, serif", fontSize: 22, marginBottom: 8 }}>Call driver</h3>
            <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              Drivers on {bus.name} ({bus.plate}). They also get a Messages note that the office is looking for them.
            </p>
            {callLoading && <div style={{ fontSize: 14, color: "#64748b", marginBottom: 12 }}>Notifying driver…</div>}
            {callError && <div style={{ fontSize: 14, color: "#dc2626", marginBottom: 12 }}>{callError}</div>}
            {callNote && !callError && <div style={{ fontSize: 14, color: "#16a34a", marginBottom: 12 }}>{callNote}</div>}
            {!callLoading && callDrivers.length === 0 && !callError && (
              <div style={{ fontSize: 14, color: "#64748b", marginBottom: 12 }}>No driver linked to this bus.</div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              {callDrivers.map((d) => {
                const phone = (d.phone || "").trim();
                const tel = phone.replace(/[^\d+]/g, "");
                return (
                  <div
                    key={d.uid}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                      padding: "12px 14px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{d.name || "Driver"}</div>
                      <div style={{ fontSize: 13, color: phone ? "#334155" : "#94a3b8", marginTop: 2 }}>
                        {phone || "No phone on file"}
                      </div>
                    </div>
                    {tel ? (
                      <a
                        href={`tel:${tel}`}
                        style={{
                          padding: "8px 14px", background: "#2563eb", color: "#fff", borderRadius: 8,
                          fontSize: 13, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap",
                        }}
                      >
                        Call
                      </a>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setShowCall(false)}
              disabled={callLoading}
              style={{
                padding: "10px 20px", background: "#f1f5f9", color: "#334155", border: "none",
                borderRadius: 8, fontSize: 14, fontWeight: 600,
                cursor: callLoading ? "not-allowed" : "pointer", fontFamily: "Geist, sans-serif",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
